import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Search, MapPin, Phone, Mail, Filter, User, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FindProvider() {
  const [providers, setProviders] = useState([]);
  const [filteredProviders, setFilteredProviders] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSpecialty, setSelectedSpecialty] = useState("all");
  const [selectedState, setSelectedState] = useState("all");
  const [selectedProvider, setSelectedProvider] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    filterProviders();
  }, [searchTerm, selectedSpecialty, selectedState, providers]);

  const fetchData = async () => {
    try {
      const [providersRes, specialtiesRes, statesRes] = await Promise.all([
        axios.get(`${API}/providers`),
        axios.get(`${API}/providers/specialties/list`),
        axios.get(`${API}/providers/states/list`),
      ]);
      setProviders(providersRes.data);
      setFilteredProviders(providersRes.data);
      setSpecialties(specialtiesRes.data.specialties);
      setStates(statesRes.data.states);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
    setLoading(false);
  };

  const filterProviders = () => {
    let filtered = providers;

    if (searchTerm) {
      filtered = filtered.filter(
        (p) =>
          p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          p.specialty.toLowerCase().includes(searchTerm.toLowerCase()) ||
          p.location.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (selectedSpecialty !== "all") {
      filtered = filtered.filter((p) => p.specialty === selectedSpecialty);
    }

    if (selectedState !== "all") {
      filtered = filtered.filter((p) => p.state === selectedState);
    }

    setFilteredProviders(filtered);
  };

  return (
    <div className="pt-20 min-h-screen bg-[#F1FAEE]">
      {/* Hero Section */}
      <section className="bg-[#14213D] py-16" data-testid="provider-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4">
              Find a Healthcare Provider
            </h1>
            <p className="text-gray-300 max-w-2xl mx-auto">
              Search our network of qualified physicians and specialists across the nation.
            </p>
          </motion.div>

          {/* Search Box */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl p-4 md:p-6 shadow-lg"
          >
            <div className="grid md:grid-cols-4 gap-4">
              <div className="md:col-span-2 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  placeholder="Search by name, specialty, or location..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 rounded-lg border-gray-200 h-12"
                  data-testid="search-input"
                />
              </div>
              <Select value={selectedSpecialty} onValueChange={setSelectedSpecialty}>
                <SelectTrigger className="h-12 rounded-lg" data-testid="specialty-filter">
                  <SelectValue placeholder="All Specialties" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Specialties</SelectItem>
                  {specialties.map((specialty) => (
                    <SelectItem key={specialty} value={specialty}>
                      {specialty}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={selectedState} onValueChange={setSelectedState}>
                <SelectTrigger className="h-12 rounded-lg" data-testid="state-filter">
                  <SelectValue placeholder="All States" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All States</SelectItem>
                  {states.map((state) => (
                    <SelectItem key={state} value={state}>
                      {state}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Results Section */}
      <section className="py-12" data-testid="provider-results">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-6">
            <p className="text-gray-600">
              Showing <span className="font-semibold text-[#14213D]">{filteredProviders.length}</span> providers
            </p>
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-500">Sort by: Relevance</span>
            </div>
          </div>

          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-white rounded-xl h-64 animate-pulse" />
              ))}
            </div>
          ) : filteredProviders.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredProviders.map((provider, index) => (
                <motion.div
                  key={provider.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white rounded-xl overflow-hidden provider-card cursor-pointer"
                  onClick={() => setSelectedProvider(provider)}
                  data-testid={`provider-${provider.id}`}
                >
                  <div className="p-6">
                    <div className="flex gap-4">
                      <img
                        src={provider.image_url}
                        alt={provider.name}
                        className="w-20 h-20 rounded-lg object-cover"
                      />
                      <div className="flex-1">
                        <h3 className="font-heading font-semibold text-lg text-[#14213D]">
                          {provider.name}
                        </h3>
                        <p className="text-[#00B4D8] text-sm font-medium">{provider.title}</p>
                        <p className="text-gray-500 text-sm">{provider.specialty}</p>
                      </div>
                    </div>

                    <div className="mt-4 space-y-2">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <MapPin className="w-4 h-4 text-gray-400" />
                        {provider.location}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Phone className="w-4 h-4 text-gray-400" />
                        {provider.phone}
                      </div>
                    </div>

                    <div className="mt-4 flex flex-wrap gap-2">
                      {provider.languages?.slice(0, 2).map((lang) => (
                        <span
                          key={lang}
                          className="specialty-badge"
                        >
                          {lang}
                        </span>
                      ))}
                      {provider.accepting_patients && (
                        <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                          Accepting Patients
                        </span>
                      )}
                    </div>

                    <Button
                      className="w-full mt-4 bg-[#F1FAEE] text-[#14213D] rounded-lg hover:bg-[#00B4D8] hover:text-white transition-colors"
                      data-testid={`view-profile-${provider.id}`}
                    >
                      View Profile
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-white rounded-xl">
              <User className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="font-heading font-semibold text-xl text-[#14213D] mb-2">
                No providers found
              </h3>
              <p className="text-gray-500">
                Try adjusting your search criteria or filters.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Provider Detail Modal */}
      {selectedProvider && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            data-testid="provider-modal"
          >
            <div className="p-6 border-b">
              <div className="flex justify-between items-start">
                <div className="flex gap-4">
                  <img
                    src={selectedProvider.image_url}
                    alt={selectedProvider.name}
                    className="w-24 h-24 rounded-xl object-cover"
                  />
                  <div>
                    <h2 className="font-heading font-bold text-2xl text-[#14213D]">
                      {selectedProvider.name}
                    </h2>
                    <p className="text-[#00B4D8] font-medium">{selectedProvider.title}</p>
                    <p className="text-gray-500">{selectedProvider.specialty}</p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedProvider(null)}
                  className="text-gray-400 hover:text-gray-600"
                  data-testid="close-modal"
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              <div>
                <h3 className="font-semibold text-[#14213D] mb-2">About</h3>
                <p className="text-gray-600">{selectedProvider.bio}</p>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <h3 className="font-semibold text-[#14213D] mb-2">Contact</h3>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-gray-600">
                      <MapPin className="w-4 h-4 text-[#00B4D8]" />
                      {selectedProvider.location}
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <Phone className="w-4 h-4 text-[#00B4D8]" />
                      {selectedProvider.phone}
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <Mail className="w-4 h-4 text-[#00B4D8]" />
                      {selectedProvider.email}
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold text-[#14213D] mb-2">Languages</h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedProvider.languages?.map((lang) => (
                      <span key={lang} className="specialty-badge">{lang}</span>
                    ))}
                  </div>
                </div>
              </div>

              {selectedProvider.education?.length > 0 && (
                <div>
                  <h3 className="font-semibold text-[#14213D] mb-2">Education</h3>
                  <ul className="list-disc list-inside text-gray-600 space-y-1">
                    {selectedProvider.education.map((edu, i) => (
                      <li key={i}>{edu}</li>
                    ))}
                  </ul>
                </div>
              )}

              {selectedProvider.certifications?.length > 0 && (
                <div>
                  <h3 className="font-semibold text-[#14213D] mb-2">Certifications</h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedProvider.certifications.map((cert, i) => (
                      <span key={i} className="px-3 py-1 bg-[#14213D]/10 text-[#14213D] rounded-full text-sm">
                        {cert}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <Button
                className="w-full bg-[#00B4D8] text-white rounded-lg py-6 hover:bg-[#0096c7]"
                data-testid="request-appointment"
              >
                Request Appointment
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
